import React, { useEffect, useRef, useState } from 'react';
import { type AdminApiClient, type JsonRecord } from './api-client';
import { ConfirmDialog } from './admin-feedback';
import {
  configurationChanges,
  configurationDraft,
  configurationError,
  configRecord,
  configText,
  productExposure,
  readConfiguration,
  recoverConfigurationChanges,
  sameConfig,
  validateConfiguration,
  type ConfigurationData,
  type ConfigurationDraft,
  type ConfigurationIssue,
  type ConfigurationSection,
} from './app-configuration-model';
import './app-configuration-workbench.css';
import { MiniappPageLinks } from './miniapp-page-links';

const sections: Array<[ConfigurationSection, string]> = [
  ['base', '基础资料'],
  ['cities', '服务城市'],
  ['products', '产品展示'],
  ['service', '客服入口'],
  ['payment', '收款商户'],
];
const sectionName = (section: ConfigurationSection) =>
  sections.find((item) => item[0] === section)?.[1] ?? '';
const money = (value: unknown) =>
  `¥${(Number(value ?? 0) / 100).toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
const productPrice = (product: JsonRecord) => {
  const skus = Array.isArray(product.skus)
    ? product.skus.map(configRecord).filter((sku) => sku.status === 'ACTIVE')
    : [];
  return skus.length
    ? `${money(Math.min(...skus.map((sku) => Number(sku.salePriceCents ?? 0))))} 起`
    : '暂无在售套餐';
};

function useDraftGuard(dirty: boolean, busy: boolean) {
  useEffect(() => {
    if (!dirty && !busy) return;
    let leaving = false;
    const originalUrl = window.location.href;
    const unload = (event: BeforeUnloadEvent) => {
      if (!leaving) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    const leave = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const anchor = target?.closest<HTMLAnchorElement>('a[href]');
      const logout = target?.closest('button')?.textContent === '退出登录';
      if (
        (!anchor && !logout) ||
        anchor?.getAttribute('href')?.startsWith('#') ||
        anchor?.target === '_blank'
      )
        return;
      if (busy || !window.confirm('修改尚未保存，确定放弃修改并离开？')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      } else leaving = true;
    };
    const pop = (event: PopStateEvent) => {
      if (leaving || window.location.href.split('#')[0] === originalUrl.split('#')[0]) return;
      if (busy || !window.confirm('修改尚未保存，确定放弃修改并离开？')) {
        event.stopImmediatePropagation();
        window.history.pushState(null, '', originalUrl);
      } else leaving = true;
    };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('popstate', pop, true);
    document.addEventListener('click', leave, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('popstate', pop, true);
      document.removeEventListener('click', leave, true);
    };
  }, [dirty, busy]);
}

function StatusText({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'good' | 'warn' | 'muted';
}) {
  return <span className={`ac-status ac-status--${tone}`}>{children}</span>;
}

export function AppConfigurationWorkbench({
  api,
  appId,
  initialSection = 'base',
}: {
  api: AdminApiClient;
  appId: string;
  initialSection?: ConfigurationSection;
}) {
  const [data, setData] = useState<ConfigurationData>();
  const [draft, setDraft] = useState<ConfigurationDraft>();
  const [saved, setSaved] = useState<ConfigurationDraft>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [feedback, setFeedback] = useState<{
    text: string;
    error: boolean;
    completed?: string[];
  }>();
  const [issues, setIssues] = useState<ConfigurationIssue[]>([]);
  const [discard, setDiscard] = useState(false);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [productFilter, setProductFilter] = useState('all');
  const [productPage, setProductPage] = useState(1);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [contactMethods, setContactMethods] = useState<string[]>([]);
  const [activeSection, setActiveSection] = useState<ConfigurationSection>(initialSection);
  const [headerHeight, setHeaderHeight] = useState(56);
  const mounted = useRef(true);
  const saving = useRef(false);
  const revision = useRef(0);
  const saveRef = useRef<() => void>(() => undefined);
  const changes = data && saved && draft ? configurationChanges(saved, draft, data) : [];
  const dirty = changes.length > 0;
  useDraftGuard(dirty, busy);

  function adopt(next: ConfigurationData, expected?: ConfigurationDraft) {
    const retained =
      next.paymentError && data
        ? {
            ...next,
            merchants: data.merchants,
            bindings: data.bindings,
            routes: next.routeError ? data.routes : next.routes,
          }
        : next;
    const nextDraft = configurationDraft(retained);
    if (next.paymentError && expected) nextDraft.merchantIds = [...expected.merchantIds];
    setData(retained);
    setSaved(nextDraft);
    setDraft(nextDraft);
    setContactMethods([
      ...(configText(nextDraft.service, 'servicePhone') ? ['phone'] : []),
      ...(['serviceWechat', 'supportPagePath', 'qrCodeUrl'].some((key) =>
        configText(nextDraft.service, key),
      )
        ? ['wechat']
        : []),
    ]);
  }
  async function reload() {
    const request = ++revision.current;
    setLoading(true);
    setLoadError('');
    try {
      const next = await readConfiguration(api, appId);
      if (mounted.current && request === revision.current) {
        adopt(next);
        setIssues([]);
        setFeedback(undefined);
      }
    } catch (error) {
      if (mounted.current && request === revision.current) setLoadError(configurationError(error));
    } finally {
      if (mounted.current && request === revision.current) setLoading(false);
    }
  }
  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => {
      mounted.current = false;
      revision.current += 1;
    };
  }, [api, appId]);
  useEffect(() => {
    const header = document.querySelector('.canonical-topbar');
    if (!header || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() =>
      setHeaderHeight(Math.ceil(header.getBoundingClientRect().height)),
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, []);
  useEffect(() => {
    if (loading || !data) return;
    if (initialSection !== 'base')
      document.getElementById(`app-config-${initialSection}`)?.scrollIntoView?.({ block: 'start' });
  }, [loading, initialSection, Boolean(data)]);
  useEffect(() => {
    if (!data) return;
    const update = () => {
      const threshold =
        headerHeight +
        (document.querySelector('.ac-command-bar')?.getBoundingClientRect().height ?? 50) +
        20;
      const elements = Array.from(document.querySelectorAll('.ac-section'));
      const active =
        elements.filter((element) => element.getBoundingClientRect().top <= threshold).at(-1) ??
        elements[0];
      if (active) setActiveSection(active.id.replace('app-config-', '') as ConfigurationSection);
    };
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [Boolean(data), headerHeight]);
  useEffect(() => setProductPage(1), [search, cityFilter, productFilter]);

  const edit = (updater: (current: ConfigurationDraft) => ConfigurationDraft) => {
    setDraft((current) => (current ? updater(current) : current));
    setFeedback(undefined);
    setIssues([]);
  };
  const changeService = (key: string, value: unknown) =>
    edit((current) => ({ ...current, service: { ...current.service, [key]: value } }));
  const issueFor = (field: string) => issues.find((issue) => issue.field === field);
  const errorProps = (field: string) => ({
    id: `ac-${field}`,
    'aria-invalid': Boolean(issueFor(field)),
    ...(issueFor(field) ? { 'aria-describedby': `ac-error-${field}` } : {}),
  });
  const errorText = (field: string) =>
    issueFor(field) ? (
      <small id={`ac-error-${field}`} className="ac-field-error">
        {issueFor(field)?.message}
      </small>
    ) : null;
  function focusIssue(issue: ConfigurationIssue) {
    setActiveSection(issue.section);
    if (issue.section === 'products') {
      setSearch('');
      setCityFilter('');
      setProductFilter('all');
      const visible =
        data?.products.filter(
          (product) =>
            draft?.cities[configText(product, 'cityId')]?.enabled ||
            !sameConfig(
              draft?.products[configText(product, 'id')],
              saved?.products[configText(product, 'id')],
            ),
        ) ?? [];
      setProductPage(
        Math.max(
          1,
          Math.floor(
            visible.findIndex((product) => `product-${configText(product, 'id')}` === issue.field) /
              12,
          ) + 1,
        ),
      );
    }
    if (issue.field === 'servicePhone')
      setContactMethods((current) => (current.includes('phone') ? current : [...current, 'phone']));
    document
      .getElementById(`app-config-${issue.section}`)
      ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    document.getElementById(`ac-${issue.field}`)?.focus({ preventScroll: true });
  }

  async function save() {
    if (!data || !draft || !saved || !dirty || saving.current || loading) return;
    const invalid = validateConfiguration(draft, data, saved);
    if (invalid.length) {
      setIssues(invalid);
      focusIssue(invalid[0]!);
      return;
    }
    saving.current = true;
    setBusy(true);
    setFeedback(undefined);
    setIssues([]);
    const completed: string[] = [];
    let currentStep = changes[0]!;
    try {
      setProgress('核对最新配置…');
      const currentData = await readConfiguration(api, appId);
      if (!mounted.current) return;
      const recovery = recoverConfigurationChanges(saved, draft, currentData);
      for (const confirmed of recovery.confirmed) {
        setSaved((previous) => (previous ? confirmed.apply(previous) : previous));
        completed.push(confirmed.label);
      }
      if (recovery.conflict) {
        currentStep = recovery.conflict;
        throw new Error(
          currentStep.id === 'payment' && currentData.paymentError
            ? '暂时无法核对商户关系，请稍后重试。'
            : '该项配置已被其他操作更新，请核对最新设置后再保存。',
        );
      }
      for (const [index, change] of recovery.pending.entries()) {
        if (!mounted.current) return;
        currentStep = change;
        setProgress(`正在保存 ${index + 1}/${recovery.pending.length} · ${change.label}`);
        await api.send(change.path, change.method, change.body);
        completed.push(change.label);
        if (mounted.current) setSaved((previous) => (previous ? change.apply(previous) : previous));
      }
      if (!mounted.current) return;
      setProgress('正在核对保存结果…');
      try {
        const verified = await readConfiguration(api, appId);
        if (!mounted.current) return;
        const confirmation = recoverConfigurationChanges(saved, draft, verified);
        if (!verified.paymentError && (confirmation.conflict || confirmation.pending.length)) {
          setData(verified);
          setSaved(configurationDraft(verified));
          setFeedback({
            text: '服务器返回的部分配置与提交内容不一致，修改已保留，请核对后重试。',
            error: true,
            completed,
          });
          return;
        }
        adopt(verified, draft);
        setFeedback(
          verified.paymentError
            ? {
                text: '修改已提交，其他配置已确认；收款商户暂未读取，已保留选择，请重新加载确认。',
                error: true,
                completed,
              }
            : { text: '配置已保存，已重新读取最新结果。', error: false },
        );
        window.dispatchEvent(new Event('travel:apps-changed'));
      } catch {
        setFeedback({
          text: '修改已提交，但最新结果读取失败。请重新加载确认。',
          error: true,
          completed,
        });
      }
    } catch (error) {
      if (!mounted.current) return;
      const message = configurationError(error);
      setFeedback({
        text: `${completed.length ? `已完成 ${completed.length} 项；` : ''}${currentStep.label}未保存：${message} 输入已保留。`,
        error: true,
        completed,
      });
      const issue = { section: currentStep.section, field: currentStep.id, message };
      setIssues([issue]);
      focusIssue(issue);
    } finally {
      saving.current = false;
      if (mounted.current) {
        setBusy(false);
        setProgress('');
      }
    }
  }
  saveRef.current = () => {
    void save();
  };

  const title = data ? configText(data.app, 'displayName', '小程序配置') : '小程序配置';
  if (!draft || !saved || !data)
    return (
      <div className="ac-workbench">
        <header className="ac-page-heading">
          <div>
            <a href="/admin/apps">多小程序管理</a>
            <h1>小程序配置</h1>
          </div>
        </header>
        <div className="ac-load" role={loadError ? 'alert' : 'status'}>
          {loadError || '正在读取配置…'}
          {loadError && (
            <button type="button" onClick={() => void reload()}>
              重新加载
            </button>
          )}
        </div>
      </div>
    );

  const enabledCities = data.cities.filter(
    (city) => draft.cities[configText(city, 'id')]?.enabled && city.status === 'ACTIVE',
  );
  const cityName = (id: string) =>
    configText(data.cities.find((city) => city.id === id) ?? {}, 'name', '未分配城市');
  const allProducts = data.products;
  const scopedProducts = allProducts.filter(
    (product) =>
      enabledCities.some((city) => city.id === product.cityId) ||
      !sameConfig(
        draft.products[configText(product, 'id')],
        saved.products[configText(product, 'id')],
      ),
  );
  const filteredProducts = scopedProducts.filter((product) => {
    const setting = draft.products[configText(product, 'id')];
    const keyword = search.trim().toLowerCase();
    return (
      (!cityFilter || product.cityId === cityFilter) &&
      (!keyword ||
        `${configText(product, 'title')} ${configText(product, 'productCode')} ${setting?.titleOverride ?? ''}`
          .toLowerCase()
          .includes(keyword)) &&
      (productFilter === 'all' ||
        (productFilter === 'selected' ? setting?.enabled : !setting?.enabled))
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / 12));
  const currentPage = Math.min(productPage, totalPages);
  const shownProducts = filteredProducts.slice((currentPage - 1) * 12, currentPage * 12);
  const shownCities = data.cities.filter((city) =>
    `${configText(city, 'name')} ${configText(city, 'code')}`
      .toLowerCase()
      .includes(citySearch.trim().toLowerCase()),
  );
  const visibleCount = scopedProducts.filter(
    (product) =>
      enabledCities.some((city) => city.id === product.cityId) &&
      draft.products[configText(product, 'id')]?.enabled &&
      product.status === 'ACTIVE',
  ).length;
  const bindingByMerchant = new Map(
    data.bindings.map((binding) => [configText(binding, 'merchantId'), binding]),
  );
  const merchants = [...data.merchants];
  for (const binding of data.bindings) {
    const merchant = configRecord(binding.merchant);
    if (merchant.id && !merchants.some((item) => item.id === merchant.id)) merchants.push(merchant);
  }
  const paymentStatus = (merchant: JsonRecord) => {
    if (data.paymentError) return { text: '待重新核对', tone: 'warn' as const };
    const id = configText(merchant, 'id');
    const binding = bindingByMerchant.get(id);
    if (!draft.merchantIds.includes(id))
      return {
        text: binding && binding.status !== 'DISABLED' ? '保存后移出' : '未关联',
        tone: 'muted' as const,
      };
    if (!saved.merchantIds.includes(id)) return { text: '保存后待核验', tone: 'warn' as const };
    if (merchant.status !== 'ACTIVE') return { text: '商户已停用', tone: 'warn' as const };
    if (!binding?.verifiedAt || binding.status === 'PENDING_VERIFY')
      return { text: '待微信核验', tone: 'warn' as const };
    if (binding.status !== 'ENABLED')
      return {
        text:
          binding.status === 'OBSERVE'
            ? '观察中'
            : binding.status === 'DEGRADED'
              ? '已降级'
              : '已停用',
        tone: 'warn' as const,
      };
    if (data.routeError) return { text: '调度状态未确认', tone: 'warn' as const };
    if (binding.cooldownUntil && new Date(String(binding.cooldownUntil)).getTime() > Date.now())
      return { text: '冷却保护中', tone: 'warn' as const };
    if (!data.routes.some((route) => route.bindingId === binding.id && route.enabled === true))
      return { text: '待配置收款调度', tone: 'warn' as const };
    return { text: '已配置收款调度', tone: 'good' as const };
  };
  const sectionHeader = (id: ConfigurationSection, extra?: React.ReactNode) => (
    <header className="ac-section-heading">
      <h2 id={`ac-heading-${id}`}>{sectionName(id)}</h2>
      {extra}
    </header>
  );

  return (
    <div
      className="ac-workbench"
      style={{ '--ac-header-height': `${headerHeight}px` } as React.CSSProperties}
    >
      <header className="ac-page-heading">
        <div>
          <a href="/admin/apps">多小程序管理</a>
          <span className="ac-breadcrumb-divider">/</span>
          <span>小程序配置</span>
          <h1>
            {title}
            <StatusText tone={saved.base.status === 'ACTIVE' ? 'good' : 'muted'}>
              {saved.base.status === 'ACTIVE' ? '已启用' : '已停用'}
            </StatusText>
          </h1>
        </div>
        <a className="ac-back" href="/admin/apps">
          返回列表
        </a>
      </header>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="ac-command-bar">
          <nav aria-label="页内配置导航">
            <a href="#app-promotion-links">推广地址</a>
            {sections.map(([id, name]) => (
              <a
                key={id}
                href={`#app-config-${id}`}
                aria-current={activeSection === id ? 'location' : undefined}
                onClick={() => setActiveSection(id)}
              >
                {name}
                {changes.some((change) => change.section === id) && <i aria-label="有未保存修改" />}
              </a>
            ))}
          </nav>
          <div className="ac-save-actions">
            <span aria-live="polite">
              {busy
                ? progress
                : dirty
                  ? `${changes.length} 项修改待保存`
                  : loadError || feedback?.error || data.paymentError
                    ? '部分结果待确认'
                    : '已与服务器同步'}
            </span>
            <button
              type="button"
              className="ac-secondary"
              disabled={busy || loading}
              onClick={() => (dirty ? setDiscard(true) : void reload())}
            >
              {dirty ? '放弃修改' : '重新加载'}
            </button>
            <button type="submit" className="primary ac-save" disabled={busy || loading || !dirty}>
              {busy ? '保存中…' : '保存配置'}
            </button>
          </div>
        </div>
        {loadError && (
          <div className="ac-feedback ac-feedback--error" role="alert">
            {loadError}，当前输入仍保留。
          </div>
        )}
        {feedback && (
          <div
            className={`ac-feedback ${feedback.error ? 'ac-feedback--error' : ''}`}
            role={feedback.error ? 'alert' : 'status'}
          >
            {feedback.text}
            {feedback.error && Boolean(feedback.completed?.length) && (
              <span>已完成：{feedback.completed!.join('、')}</span>
            )}
          </div>
        )}
        {issues.length > 0 && (
          <div className="ac-validation" role="alert">
            {issues.map((issue) => (
              <button
                key={`${issue.field}-${issue.message}`}
                type="button"
                onClick={() => focusIssue(issue)}
              >
                {sectionName(issue.section)}：{issue.message}
              </button>
            ))}
          </div>
        )}
        <fieldset className="ac-fields" disabled={busy || loading}>
          <section id="app-config-base" className="ac-section" aria-labelledby="ac-heading-base">
            {sectionHeader('base')}
            <div className="ac-section-content ac-grid">
              <label>
                小程序名称 <b className="ac-required">*</b>
                <input
                  {...errorProps('displayName')}
                  maxLength={100}
                  value={draft.base.displayName}
                  onChange={(event) =>
                    edit((current) => ({
                      ...current,
                      base: { ...current.base, displayName: event.target.value },
                    }))
                  }
                />
                {errorText('displayName')}
              </label>
              <label>
                微信 AppID
                <input
                  value={
                    data.app.isFixture === true &&
                    draft.base.wechatAppIdRef === saved.base.wechatAppIdRef
                      ? ''
                      : draft.base.wechatAppIdRef
                  }
                  placeholder={
                    data.app.isFixture === true
                      ? '开发测试配置；正式接入时填写'
                      : 'wx 开头的小程序 AppID'
                  }
                  onChange={(event) =>
                    edit((current) => ({
                      ...current,
                      base: { ...current.base, wechatAppIdRef: event.target.value },
                    }))
                  }
                />
              </label>
              <label>
                运营状态
                <select
                  value={draft.base.status}
                  onChange={(event) =>
                    edit((current) => ({
                      ...current,
                      base: { ...current.base, status: event.target.value },
                    }))
                  }
                >
                  <option value="ACTIVE">启用</option>
                  <option value="DISABLED">停用</option>
                </select>
              </label>
              <label>
                默认下单方式
                <select
                  value={draft.home.defaultOrderMode}
                  onChange={(event) =>
                    edit((current) => ({
                      ...current,
                      home: { ...current.home, defaultOrderMode: event.target.value },
                    }))
                  }
                >
                  <option value="STANDARD">先填资料，再支付</option>
                  <option value="QUICK">先支付，再补资料</option>
                </select>
              </label>
              <label>
                品牌主色
                <div className="ac-color">
                  <input
                    aria-label="选择品牌色"
                    type="color"
                    value={
                      /^#[0-9a-f]{6}$/i.test(draft.home.primaryColor)
                        ? draft.home.primaryColor
                        : '#16a085'
                    }
                    onChange={(event) =>
                      edit((current) => ({
                        ...current,
                        home: { ...current.home, primaryColor: event.target.value },
                      }))
                    }
                  />
                  <input
                    {...errorProps('primaryColor')}
                    aria-label="品牌色值"
                    value={draft.home.primaryColor}
                    maxLength={7}
                    onChange={(event) =>
                      edit((current) => ({
                        ...current,
                        home: { ...current.home, primaryColor: event.target.value },
                      }))
                    }
                  />
                </div>
                {errorText('primaryColor')}
              </label>
            </div>
          </section>
          <section
            id="app-config-cities"
            className="ac-section"
            aria-labelledby="ac-heading-cities"
          >
            {sectionHeader('cities', <span>{enabledCities.length} 个已开放</span>)}
            <div className="ac-section-content">
              <div className="ac-city-tools">
                <label>
                  首页默认城市 <b className="ac-required">*</b>
                  <select
                    {...errorProps('defaultCityId')}
                    value={draft.home.defaultCityId}
                    onChange={(event) =>
                      edit((current) => ({
                        ...current,
                        home: { ...current.home, defaultCityId: event.target.value },
                      }))
                    }
                  >
                    <option value="">请选择已开放城市</option>
                    {enabledCities.map((city) => (
                      <option key={configText(city, 'id')} value={configText(city, 'id')}>
                        {configText(city, 'name')}
                      </option>
                    ))}
                  </select>
                  {errorText('defaultCityId')}
                </label>
                <label>
                  查找城市
                  <input
                    type="search"
                    value={citySearch}
                    placeholder="城市名称"
                    onChange={(event) => setCitySearch(event.target.value)}
                  />
                </label>
                <span>排序数值越小，城市越靠前</span>
              </div>
              <div className="ac-cities" role="group" aria-label="服务城市开放范围">
                {shownCities.map((city) => {
                  const id = configText(city, 'id');
                  const value = draft.cities[id]!;
                  const isDefault = draft.home.defaultCityId === id;
                  return (
                    <div key={id} className={`ac-city ${value.enabled ? 'is-selected' : ''}`}>
                      <label className="ac-check">
                        <input
                          type="checkbox"
                          aria-label={`开放${configText(city, 'name')}`}
                          checked={value.enabled}
                          disabled={city.status !== 'ACTIVE' && !value.enabled}
                          onChange={(event) => {
                            const enabled = event.target.checked;
                            edit((current) => ({
                              ...current,
                              cities: {
                                ...current.cities,
                                [id]: { ...current.cities[id]!, enabled },
                              },
                              home: {
                                ...current.home,
                                defaultCityId:
                                  !enabled && isDefault
                                    ? ''
                                    : current.home.defaultCityId || (enabled ? id : ''),
                              },
                            }));
                          }}
                        />
                        <span>{configText(city, 'name')}</span>
                      </label>
                      {isDefault ? (
                        <small className="ac-default">默认</small>
                      ) : city.status !== 'ACTIVE' ? (
                        <small>已停用</small>
                      ) : null}
                      {value.enabled && (
                        <label
                          className="ac-city-navigation"
                          title="控制该城市首页是否显示整条城市切换导航，不影响城市开放状态"
                        >
                          <input
                            type="checkbox"
                            aria-label={`${configText(city, 'name')}显示城市导航`}
                            checked={value.showCityNavigation !== false}
                            onChange={(event) => {
                              const showCityNavigation = event.target.checked;
                              edit((current) => ({
                                ...current,
                                cities: {
                                  ...current.cities,
                                  [id]: { ...current.cities[id]!, showCityNavigation },
                                },
                              }));
                            }}
                          />
                          <span>显示城市导航</span>
                        </label>
                      )}
                      {value.enabled && (
                        <input
                          {...errorProps(`city-${id}`)}
                          className="ac-sort"
                          type="number"
                          min={0}
                          max={99999}
                          aria-label={`${configText(city, 'name')}城市排序`}
                          value={Number.isNaN(value.sortOrder) ? '' : value.sortOrder}
                          onChange={(event) =>
                            edit((current) => ({
                              ...current,
                              cities: {
                                ...current.cities,
                                [id]: {
                                  ...current.cities[id]!,
                                  sortOrder:
                                    event.target.value === '' ? NaN : Number(event.target.value),
                                },
                              },
                            }))
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              {!shownCities.length && <p className="ac-empty">没有匹配的城市</p>}
            </div>
          </section>
          <section
            id="app-config-products"
            className="ac-section"
            aria-labelledby="ac-heading-products"
          >
            {sectionHeader('products', <span>{visibleCount} 个产品设为展示</span>)}
            <div className="ac-section-content">
              <div className="ac-table-tools">
                <input
                  type="search"
                  aria-label="查找配置产品"
                  placeholder="产品名称 / 编码"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <select
                  aria-label="按服务城市筛选产品"
                  value={cityFilter}
                  onChange={(event) => setCityFilter(event.target.value)}
                >
                  <option value="">全部开放城市</option>
                  {enabledCities.map((city) => (
                    <option key={configText(city, 'id')} value={configText(city, 'id')}>
                      {configText(city, 'name')}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="按展示选择筛选产品"
                  value={productFilter}
                  onChange={(event) => setProductFilter(event.target.value)}
                >
                  <option value="all">全部产品</option>
                  <option value="selected">已勾选展示</option>
                  <option value="unselected">未勾选展示</option>
                </select>
                <span>{filteredProducts.length} 个产品</span>
              </div>
              <div className="ac-table-scroll">
                <table className="ac-product-table">
                  <thead>
                    <tr>
                      <th className="ac-check-col">展示</th>
                      <th>产品 / 城市</th>
                      <th>在售价格</th>
                      <th>小程序专属名称</th>
                      <th>展示状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownProducts.map((product) => {
                      const id = configText(product, 'id');
                      const setting = draft.products[id]!;
                      const exposure = productExposure(product, draft, data);
                      return (
                        <tr key={id} className={setting.enabled ? 'is-selected' : ''}>
                          <td>
                            <input
                              id={`ac-product-${id}`}
                              type="checkbox"
                              aria-label={`展示${configText(product, 'title')}`}
                              checked={setting.enabled}
                              onChange={(event) =>
                                edit((current) => ({
                                  ...current,
                                  products: {
                                    ...current.products,
                                    [id]: {
                                      ...current.products[id]!,
                                      enabled: event.target.checked,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <strong>{configText(product, 'title')}</strong>
                            <small>
                              {cityName(configText(product, 'cityId'))} ·{' '}
                              {configText(product, 'productCode')}
                            </small>
                          </td>
                          <td className="ac-price">{productPrice(product)}</td>
                          <td>
                            <input
                              aria-label={`${configText(product, 'title')}专属名称`}
                              disabled={!setting.enabled}
                              maxLength={200}
                              value={setting.titleOverride}
                              placeholder="使用产品原名"
                              onChange={(event) =>
                                edit((current) => ({
                                  ...current,
                                  products: {
                                    ...current.products,
                                    [id]: {
                                      ...current.products[id]!,
                                      titleOverride: event.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            <StatusText
                              tone={
                                exposure === '已设为展示'
                                  ? 'good'
                                  : exposure === '不展示'
                                    ? 'muted'
                                    : 'warn'
                              }
                            >
                              {exposure}
                            </StatusText>
                            {!sameConfig(setting, saved.products[id]) && (
                              <button
                                className="ac-revert-row"
                                type="button"
                                aria-label={`撤销${configText(product, 'title')}的修改`}
                                onClick={() =>
                                  edit((current) => ({
                                    ...current,
                                    products: {
                                      ...current.products,
                                      [id]: { ...saved.products[id]! },
                                    },
                                  }))
                                }
                              >
                                撤销修改
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!shownProducts.length && (
                <p className="ac-empty">
                  {enabledCities.length
                    ? '当前条件下没有产品'
                    : '请先选择服务城市，随后可配置该城市的产品。'}
                </p>
              )}
              {totalPages > 1 && (
                <div className="ac-pagination">
                  <span>
                    第 {currentPage} / {totalPages} 页
                  </span>
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setProductPage(currentPage - 1)}
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setProductPage(currentPage + 1)}
                  >
                    下一页
                  </button>
                </div>
              )}
            </div>
          </section>
          <section
            id="app-config-service"
            className="ac-section"
            aria-labelledby="ac-heading-service"
          >
            {sectionHeader('service')}
            <div className="ac-section-content">
              <div className="ac-grid" style={{ marginBottom: 20 }}>
                <label>
                  营业执照（线路详情页展示）
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    disabled={busy}
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (!file) return;
                      if (
                        !['image/jpeg', 'image/png'].includes(file.type) ||
                        file.size > 5 * 1024 * 1024
                      ) {
                        setFeedback({ text: '请上传 5MB 以内的 JPEG 或 PNG 图片', error: true });
                        return;
                      }
                      setBusy(true);
                      try {
                        const form = new FormData();
                        form.append('file', file);
                        const result = await api.upload<{ url: string }>(
                          `/api/admin/m2/apps/${appId}/qualification/upload`,
                          form,
                          `qualification-${crypto.randomUUID()}`,
                        );
                        if (mounted.current) {
                          changeService('businessLicenseUrl', result.url);
                          setFeedback({
                            text: '营业执照已上传，请保存配置后在小程序展示',
                            error: false,
                          });
                        }
                      } catch (error) {
                        if (mounted.current)
                          setFeedback({
                            text: error instanceof Error ? error.message : '上传失败，请重试',
                            error: true,
                          });
                      } finally {
                        if (mounted.current) setBusy(false);
                      }
                    }}
                  />
                  <small>仅上传可公开展示的营业执照，JPEG / PNG，最大 5MB。</small>
                </label>
                {configText(draft.service, 'businessLicenseUrl') && (
                  <div>
                    <p>营业执照图片已配置</p>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => changeService('businessLicenseUrl', '')}
                    >
                      移除营业执照
                    </button>
                  </div>
                )}
              </div>
              <label className="ac-check ac-service-toggle">
                <input
                  type="checkbox"
                  checked={draft.service.enabled === true}
                  onChange={(event) => {
                    changeService('enabled', event.target.checked);
                    if (event.target.checked && !contactMethods.length)
                      setContactMethods(['phone']);
                  }}
                />
                启用客服与售后入口
              </label>
              {draft.service.enabled === true && (
                <div className="ac-service-options">
                  <div className="ac-grid">
                    <label>
                      客服名称 <b className="ac-required">*</b>
                      <input
                        {...errorProps('serviceName')}
                        value={configText(draft.service, 'displayName')}
                        onChange={(event) => changeService('displayName', event.target.value)}
                      />
                      {errorText('serviceName')}
                    </label>
                    <label>
                      服务时间 <b className="ac-required">*</b>
                      <input
                        {...errorProps('serviceHours')}
                        value={configText(draft.service, 'serviceHours')}
                        placeholder="例如 09:00–18:00"
                        onChange={(event) => changeService('serviceHours', event.target.value)}
                      />
                      {errorText('serviceHours')}
                    </label>
                    <label>
                      联系提示
                      <input
                        value={configText(draft.service, 'contactHint')}
                        placeholder="选填"
                        onChange={(event) => changeService('contactHint', event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="ac-contact-methods">
                    <span>联系方式</span>
                    {[
                      ['phone', '电话'],
                      ['wechat', '企业微信客服'],
                    ].map(([key, name]) => (
                      <label className="ac-check" key={key}>
                        <input
                          type="checkbox"
                          checked={contactMethods.includes(key!)}
                          onChange={(event) => {
                            setContactMethods((current) =>
                              event.target.checked
                                ? [...current, key!]
                                : current.filter((item) => item !== key),
                            );
                            if (!event.target.checked)
                              edit((current) => ({
                                ...current,
                                service: {
                                  ...current.service,
                                  ...(key === 'phone'
                                    ? { servicePhone: '' }
                                    : { serviceWechat: '', supportPagePath: '', qrCodeUrl: '' }),
                                },
                              }));
                          }}
                        />
                        {name}
                      </label>
                    ))}
                  </div>
                  <div className="ac-grid">
                    {contactMethods.includes('phone') && (
                      <label>
                        客服电话
                        <input
                          {...errorProps('servicePhone')}
                          type="tel"
                          value={configText(draft.service, 'servicePhone')}
                          onChange={(event) => changeService('servicePhone', event.target.value)}
                        />
                        {errorText('servicePhone')}
                      </label>
                    )}
                    {contactMethods.includes('wechat') && (
                      <>
                        <label>
                          企业 ID（CorpID）
                          <input
                            {...errorProps('serviceWechat')}
                            placeholder="企业微信后台的企业 ID"
                            value={configText(draft.service, 'serviceWechat')}
                            onChange={(event) => changeService('serviceWechat', event.target.value)}
                          />
                          {errorText('serviceWechat')}
                        </label>
                        <label>
                          微信客服链接
                          <input
                            {...errorProps('supportPagePath')}
                            value={configText(draft.service, 'supportPagePath')}
                            placeholder="https://work.weixin.qq.com/kfid/…"
                            onChange={(event) =>
                              changeService('supportPagePath', event.target.value)
                            }
                          />
                          {errorText('supportPagePath')}
                        </label>
                        <label>
                          微信二维码地址
                          <input
                            {...errorProps('qrCodeUrl')}
                            value={configText(draft.service, 'qrCodeUrl')}
                            placeholder="HTTPS 图片地址，选填"
                            onChange={(event) => changeService('qrCodeUrl', event.target.value)}
                          />
                          {errorText('qrCodeUrl')}
                        </label>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
          <section
            id="app-config-payment"
            className="ac-section"
            aria-labelledby="ac-heading-payment"
          >
            {sectionHeader('payment', <span>{draft.merchantIds.length} 个已选择</span>)}
            <div className="ac-section-content">
              <div className="ac-table-tools">
                <input
                  type="search"
                  aria-label="查找收款商户"
                  placeholder="商户名称 / 商户号"
                  value={merchantSearch}
                  onChange={(event) => setMerchantSearch(event.target.value)}
                />
                <span>新增或重新加入的商户需核验微信授权</span>
                <a href="/admin/payments/bindings">授权与调度设置 ↗</a>
              </div>
              {data.paymentError && (
                <div className="ac-feedback ac-feedback--error" role="alert">
                  {data.paymentError}
                  <button type="button" onClick={() => (dirty ? setDiscard(true) : void reload())}>
                    重新加载
                  </button>
                </div>
              )}
              {(!data.paymentError || merchants.length > 0) && (
                <div className="ac-table-scroll">
                  <table className="ac-merchant-table">
                    <thead>
                      <tr>
                        <th className="ac-check-col">关联</th>
                        <th>支付商户</th>
                        <th>微信授权</th>
                        <th>收款调度</th>
                      </tr>
                    </thead>
                    <tbody>
                      {merchants
                        .filter((merchant) =>
                          `${configText(merchant, 'displayName')} ${configText(merchant, 'mchid')}`
                            .toLowerCase()
                            .includes(merchantSearch.trim().toLowerCase()),
                        )
                        .map((merchant) => {
                          const id = configText(merchant, 'id');
                          const binding = bindingByMerchant.get(id);
                          const status = paymentStatus(merchant);
                          return (
                            <tr key={id}>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`关联${configText(merchant, 'displayName')}`}
                                  checked={draft.merchantIds.includes(id)}
                                  disabled={
                                    Boolean(data.paymentError) ||
                                    (merchant.status !== 'ACTIVE' &&
                                      !draft.merchantIds.includes(id))
                                  }
                                  onChange={(event) =>
                                    edit((current) => ({
                                      ...current,
                                      merchantIds: event.target.checked
                                        ? [...current.merchantIds, id].sort()
                                        : current.merchantIds.filter((item) => item !== id),
                                    }))
                                  }
                                />
                              </td>
                              <td>
                                <strong>{configText(merchant, 'displayName')}</strong>
                                <small>{configText(merchant, 'mchid')}</small>
                              </td>
                              <td>
                                {binding?.verifiedAt && saved.merchantIds.includes(id)
                                  ? '已核验'
                                  : '未核验'}
                              </td>
                              <td>
                                <StatusText tone={status.tone}>{status.text}</StatusText>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  {!merchants.length && (
                    <p className="ac-empty">
                      暂无商户，<a href="/admin/payments/merchants">前往支付中心添加</a>
                    </p>
                  )}
                </div>
              )}
              {data.routeError && <p className="ac-field-error">{data.routeError}</p>}
            </div>
          </section>
        </fieldset>
      </form>
      <div id="app-promotion-links">
        <MiniappPageLinks api={api} appId={appId} targetType="HOME" title="首页推广地址" />
      </div>
      <ConfirmDialog
        open={discard}
        title="放弃尚未保存的修改？"
        description="已保存的配置不受影响，页面将重新读取服务器上的最新设置。"
        confirmLabel="放弃并重新加载"
        onCancel={() => setDiscard(false)}
        onConfirm={() => {
          setDiscard(false);
          setFeedback(undefined);
          void reload();
        }}
      />
    </div>
  );
}
